import { Close } from "assets/icons/Close";
import { Button } from "components/Button";
import { SubmitHandler, useForm } from "react-hook-form";
import { campaignsApi } from "screens/Campaigns/service";
import { ICampaign, IProject } from "screens/Campaigns/types";
import { ProjectFormInput } from "screens/DApps/components/Pannel/components/ProjectFormInput";
import twitter from "assets/icons/twitter.png";
import ImageIcon from "assets/icons/ImageIcon";
import cn from "clsx";
import { useEffect, useState } from "react";
import ImagePlaceholder from "assets/images/dapps/imagePlaceholder.png";
import { MIN_IMG_FILE_SIZE } from "screens/DApps/constants";
import { fileApi } from "services/files";
import { useCampaignsStore } from "screens/Campaigns/store";
import { CampaignsDropdown } from "../CampaignsDropdown";
import { SquareLoader } from "screens/Main/components/Loader/SquareLoader";

export const ProjectsAddForm = ({
  isOpen,
  onClose,
  getAllProjectsHandler
}: {
  isOpen: boolean;
  onClose: () => void;
  getAllProjectsHandler: () => void;
}) => {
  const [projectLogoPreview, setProjectLogoPreview] =
    useState<string>(ImagePlaceholder);
  const [projectLogoFile, setProjectLogoFile] = useState<File | null>(null);
  const [projectLogoSizeError, setProjectLogoSizeError] = useState(false);
  const {
    campaigns,
    hasMoreCampaigns,
    selectedProject,
    isLoadMoreLoading,
    setIsLoadMoreLoading,
    setSelectedProject,
    setCampaigns
  } = useCampaignsStore();
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [campaignsPage, setCampaignsPage] = useState(1);

  const { handleSubmit, watch, reset, control, setValue } = useForm<IProject>({
    defaultValues: {
      title: "",
      x_url: "",
      project_url: "",
      campaignId: ""
    },
    mode: "all"
  });

  const project_url = watch("project_url");

  // Fill form with selected project data when it changes
  useEffect(() => {
    if (selectedProject && isOpen) {
      setValue("title", selectedProject.title || "");
      setValue("x_url", selectedProject.x_url || "");
      setValue("project_url", selectedProject.project_url || "");
      setValue("campaignId", selectedProject.campaignId || "");
    }
  }, [selectedProject, isOpen, setValue]);

  const handleLoadMoreCampaigns = async () => {
    if (!hasMoreCampaigns || isLoadMoreLoading) return;

    try {
      setIsLoadMoreLoading(true);
      const nextPage = campaignsPage + 1;
      const newCampaigns = await campaignsApi.getAllCampaigns(nextPage);
      if (newCampaigns) {
        setCampaigns([...campaigns, ...newCampaigns]);
        setCampaignsPage(nextPage);
      }
    } catch (error) {
      console.error("Error loading more campaigns", error);
    } finally {
      setIsLoadMoreLoading(false);
    }
  };

  const onSubmit: SubmitHandler<IProject> = (data) => {
    if (selectedProject) {
      // Update existing project
      campaignsApi.updateProject({ ...data, id: selectedProject.id }, () => {
        setProjectLogoPreview(ImagePlaceholder);
        reset();
        setSelectedProject(null);
        onClose();
        getAllProjectsHandler();
      });
    } else {
      // Create new project
      campaignsApi.createProject({ ...data }, () => {
        setProjectLogoPreview(ImagePlaceholder);
        reset();
        onClose();
        getAllProjectsHandler();
      });
    }
  };

  // Reset form and selected project on close
  useEffect(() => {
    if (!isOpen) {
      reset();
      setSelectedProject(null);
    }
  }, [isOpen, reset, setSelectedProject]);

  const isSubmitDisabled =
    !watch("title") ||
    !watch("x_url") ||
    !watch("project_url") ||
    !watch("campaignId") ||
    isUploadingLogo;

  const selectedCampaign = watch("campaignId");

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > MIN_IMG_FILE_SIZE) {
        setProjectLogoSizeError(true);
        return;
      }
      setProjectLogoSizeError(false);
      setProjectLogoFile(file);
    }
  };

  const handleCampaignClick = (campaign: ICampaign) => {
    setValue("campaignId", campaign.id);
  };

  const uploadProjectLogo = async (file: File) => {
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    setProjectLogoSizeError(false);

    if (!allowedTypes.includes(file.type)) {
      setProjectLogoSizeError(true);
      return;
    }

    setIsUploadingLogo(true);
    const url = await fileApi.uploadImage(file);
    setValue("project_url", url);
    setIsUploadingLogo(false);
  };

  useEffect(() => {
    projectLogoFile && uploadProjectLogo(projectLogoFile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectLogoFile]);

  return (
    <>
      {isOpen && (
        <div
          className="fixed z-4 bg-black/40 top-0 left-0 right-0 bottom-0 w-full"
          onClick={onClose}
        />
      )}
      <div
        className={`fixed w-full md:w-[487px] h-[100svh] overflow-y-auto custom-scrollbar px-[20px] md:px-[40px] pt-[60px] top-0 bottom-0 right-0 bg-black md:border-l-[1px] z-5 transform transition-transform duration-300 ${isOpen ? "translate-x-0" : "translate-x-full"
          }`}
      >
        <div className="flex text-[24px] font-[700] leading-[32px] justify-between items-center mb-[40px]">
          <h3>{selectedProject ? "Edit project" : "Add new project"}</h3>
          <button onClick={onClose}>
            <Close />
          </button>
        </div>

        <form
          id="project-add-form"
          onSubmit={handleSubmit(onSubmit)}
          className={`flex flex-col gap-[24px] h-fit`}
        >
          <div>
            <p className="ml-1 mb-1 text-[12px] leading-[17px] font-[500] text-[#FFFFFF99]">
              Project logo
            </p>
            <div className="flex flex-col gap-[24px] w-full">
              <div
                className={cn(
                  "py-4 w-20 h-20 rounded-full overflow-hidden custom-scrollbar flex justify-center items-center bg-[#FFFFFF1A]",
                  {
                    "!py-0": projectLogoFile
                  }
                )}
              >
                <img
                  src={
                    project_url ||
                    selectedProject?.project_url ||
                    projectLogoPreview
                  }
                  alt=""
                  className="w-20 h-20 object-contain object-center"
                />
              </div>

              <div className="flex flex-col gap-[8px] justify-center">
                <input
                  id="project-logo"
                  type="file"
                  accept=".jpeg, .jpg, .png"
                  style={{ display: "none" }}
                  onChange={handleLogoFileChange}
                />
                <label
                  htmlFor="project-logo"
                  className="w-[175px] h-[43px] bg-[#FFFFFF1A] flex items-center justify-center rounded-full"
                >
                  <button
                    type="button"
                    className="px-3 flex items-center justify-center gap-[10px] text-[14px] font-[500] leading-[18.9px]"
                    disabled={isUploadingLogo}
                    onClick={() =>
                      document.getElementById("project-logo")?.click()
                    }
                  >
                    {isUploadingLogo ? (
                      <div className="flex w-full my-[20px] justify-center items-center">
                        <SquareLoader size={16} />
                      </div>
                    ) : (
                      <>
                        <ImageIcon />
                        Upload project logo
                      </>
                    )}
                  </button>
                </label>
                <p
                  className={`text-[12px] font-[500] ${projectLogoSizeError ? "text-red-500" : "opacity-70"
                    }`}
                >
                  {projectLogoSizeError
                    ? "Maximum image size 5 MB"
                    : "max 5mb, .jpeg, .png"}
                </p>
              </div>
            </div>
          </div>
          <div>
            <p className="ml-1 mb-1 text-[12px] leading-[17px] font-[500] text-[#FFFFFF99]">
              Select campaign
            </p>

            <CampaignsDropdown
              campaigns={campaigns}
              onCampaignClick={(campaign) => handleCampaignClick(campaign)}
              hasMore={hasMoreCampaigns}
              onLoadMoreClick={ handleLoadMoreCampaigns }
            >
              <div className="capitalize cursor-pointer bg-[#FFFFFF1A] custom-scrollbar overflow-x-auto h-[43px] rounded-[8px] flex items-center gap-1 px-[20px]">
                {selectedCampaign &&
                  campaigns.find((c) => c.id === selectedCampaign)?.title}
                {!selectedCampaign && (
                  <span className="text-[#FFFFFF80]">Select campaign</span>
                )}
              </div>
            </CampaignsDropdown>
          </div>
          <ProjectFormInput
            title="Project title"
            placeholder="Add title here"
            control={control}
            name="title"
          />
          <ProjectFormInput
            title="Project twitter URL"
            placeholder="Add X url here"
            startElement={
              <img
                src={twitter}
                alt=""
                className="h-[16px] w-[16px] mr-[10px]"
              />
            }
            control={control}
            name="x_url"
          />
        </form>

        <div className="bg-gradient-to-t from-black to-transparent pb-[40px] pt-[60px] flex items-center w-full left-0 right-0 z-10">
          <Button
            extraClass="w-full"
            type="submit"
            form="project-add-form"
            disabled={isSubmitDisabled}
          >
            <span className="mr-[10px] text-[18px]">
              {selectedProject ? "✓" : "+"}
            </span>{" "}
            {selectedProject ? "Update project" : "Add new project"}
          </Button>
        </div>
      </div>
    </>
  );
};
